/**
 * Stoffliste und Betriebsmodi einer Anlage – in beiden Betriebsarten des Dialogs.
 *
 * **Bearbeiten:** Die Zeilen liegen am Server und werden dort einzeln gepflegt; jede Änderung
 * wirkt sofort und nur auf diese Anlage.
 *
 * **Anlegen:** Die Anlage existiert noch nicht, also auch ihre Zeilen nicht. Der Hook hält sie
 * im Speicher, vorbelegt aus dem gewählten Anlagentyp, und der Anlegen-Aufruf schickt sie als
 * Abweichung mit – was dort steht, gilt für alle Anlagen, die in einem Zug entstehen.
 *
 * Beide Betriebsarten bieten dieselben vier Rückrufe an, damit die Listen-Abschnitte nicht
 * wissen müssen, in welcher sie gerade laufen.
 */
import { useCallback, useEffect, useState } from "react";
import {
  BetriebsangabenFelder,
  BetriebsangabenUpdate,
  GenerationUnitBetriebsmodus,
  GenerationUnitStoff,
  generationUnitsApi,
} from "../../../../services/api/generationUnits";
import {
  BetriebsmodusCreate,
  StoffCreate,
  StoffUpdate,
  anlagentypenApi,
} from "../../../../services/api/anlagentypen";

/** Vorläufige ID für eine Zeile, die es auf dem Server noch nicht gibt */
const neueId = () => `neu-${Math.random().toString(36).slice(2)}`;

interface Optionen {
  projectId: string;
  /** Beim Anlegen `null` */
  unitId: string | null;
  /** Beim Anlegen: der gewählte Anlagentyp, aus dem vorbelegt wird */
  anlagentypId: string | null;
}

export const useAnlageListen = ({
  projectId,
  unitId,
  anlagentypId,
}: Optionen) => {
  const [stoffe, setStoffe] = useState<GenerationUnitStoff[]>([]);
  const [betriebsmodi, setBetriebsmodi] = useState<
    GenerationUnitBetriebsmodus[]
  >([]);
  const [loading, setLoading] = useState(false);

  const imAnlegen = !unitId;

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      if (unitId) {
        const [s, m] = await Promise.all([
          generationUnitsApi.getStoffe(projectId, unitId),
          generationUnitsApi.getBetriebsmodi(projectId, unitId),
        ]);
        setStoffe(s);
        setBetriebsmodi(m);
      } else if (anlagentypId) {
        // Vorbelegung aus der Vorlage: dieselben Zeilen, die das Anlegen sonst kopieren würde.
        const [s, m] = await Promise.all([
          anlagentypenApi.getStoffe(anlagentypId),
          anlagentypenApi.getBetriebsmodi(anlagentypId),
        ]);
        setStoffe(s.map((zeile) => ({ ...zeile, generation_unit_id: "" })));
        setBetriebsmodi(
          m.map((zeile) => ({ ...zeile, generation_unit_id: "" })),
        );
      } else {
        setStoffe([]);
        setBetriebsmodi([]);
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, unitId, anlagentypId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const createStoff = useCallback(
    async (werte: StoffCreate) => {
      if (unitId) {
        await generationUnitsApi.createStoff(projectId, unitId, werte);
        await laden();
        return;
      }
      setStoffe((vorher) => [
        ...vorher,
        {
          ...werte,
          id: neueId(),
          generation_unit_id: "",
        } as GenerationUnitStoff,
      ]);
    },
    [projectId, unitId, laden],
  );

  const updateStoff = useCallback(
    async (stoffId: string, werte: StoffUpdate) => {
      if (unitId) {
        await generationUnitsApi.updateStoff(projectId, unitId, stoffId, werte);
        await laden();
        return;
      }
      setStoffe((vorher) =>
        vorher.map((s) => (s.id === stoffId ? { ...s, ...werte } : s)),
      );
    },
    [projectId, unitId, laden],
  );

  const deleteStoff = useCallback(
    async (stoffId: string) => {
      if (unitId) {
        await generationUnitsApi.deleteStoff(projectId, unitId, stoffId);
        await laden();
        return;
      }
      setStoffe((vorher) => vorher.filter((s) => s.id !== stoffId));
    },
    [projectId, unitId, laden],
  );

  const createBetriebsmodus = useCallback(
    async (werte: BetriebsmodusCreate & BetriebsangabenFelder) => {
      if (unitId) {
        await generationUnitsApi.createBetriebsmodus(projectId, unitId, werte);
        await laden();
        return;
      }
      setBetriebsmodi((vorher) => [
        ...vorher,
        {
          ...werte,
          id: neueId(),
          generation_unit_id: "",
        } as GenerationUnitBetriebsmodus,
      ]);
    },
    [projectId, unitId, laden],
  );

  const deleteBetriebsmodus = useCallback(
    async (modusId: string) => {
      if (unitId) {
        await generationUnitsApi.deleteBetriebsmodus(
          projectId,
          unitId,
          modusId,
        );
        await laden();
        return;
      }
      setBetriebsmodi((vorher) => vorher.filter((m) => m.id !== modusId));
    },
    [projectId, unitId, laden],
  );

  const updateBetriebsmodus = useCallback(
    async (modusId: string, werte: BetriebsangabenUpdate) => {
      if (unitId) {
        await generationUnitsApi.updateBetriebsmodus(
          projectId,
          unitId,
          modusId,
          werte,
        );
        await laden();
        return;
      }
      setBetriebsmodi((vorher) =>
        vorher.map((m) => (m.id === modusId ? { ...m, ...werte } : m)),
      );
    },
    [projectId, unitId, laden],
  );

  return {
    stoffe,
    betriebsmodi,
    loading,
    imAnlegen,
    createStoff,
    updateStoff,
    deleteStoff,
    createBetriebsmodus,
    updateBetriebsmodus,
    deleteBetriebsmodus,
  };
};
